import { metricKeys, type AccountingFrame, type GameEvent, type GameStateSnapshot, type LiveRateSnapshot, type SessionAggregate, type SessionIngestResult, type SessionRecord, type ValidatedObservation } from '@/domain/types'
import type { GamePreset } from '@/presets/types'
import { SessionExpCounter } from './session-exp-counter'
import { TrustedMetricLedger } from './trusted-metric-ledger'

const emptyCoverage = () => Object.fromEntries(metricKeys.map((key) => [key, 0])) as SessionAggregate['coverage']

export class SessionEngine {
  private record: SessionRecord | null = null
  private pauseStartedAt: number | null = null
  private accumulatedPauseMs = 0
  private latestState: GameStateSnapshot | null = null
  private captureGeneration = 0
  private readonly ledger: TrustedMetricLedger
  private readonly expCounter: SessionExpCounter
  private latestAccounting: AccountingFrame | null = null

  constructor(private readonly preset: GamePreset) {
    this.ledger = new TrustedMetricLedger(preset)
    this.expCounter = new SessionExpCounter(preset)
  }

  start(
    profileId: string,
    now = Date.now(),
    initialState: GameStateSnapshot | null = null,
    captureGeneration = 0,
  ): SessionRecord {
    if (this.record && this.record.status !== 'ended') throw new Error('A record is already active')
    this.pauseStartedAt = null
    this.accumulatedPauseMs = 0
    this.latestState = initialState
    this.latestAccounting = null
    this.captureGeneration = captureGeneration
    this.ledger.reset(now, captureGeneration, initialState)
    this.expCounter.reset(initialState)
    this.record = {
      id: crypto.randomUUID(),
      presetId: this.preset.id,
      presetVersion: this.preset.version,
      profileId,
      status: 'running',
      startedAt: now,
      activeDurationMs: 0,
      aggregate: this.emptyAggregate(),
      ...(initialState ? { startState: initialState, endState: initialState } : {}),
    }
    return this.update({}, now)
  }

  pause(now = Date.now()): SessionRecord {
    this.assertStatus('running')
    this.pauseStartedAt = now
    this.ledger.suspend()
    this.expCounter.breakContinuity()
    return this.update({ status: 'paused', pausedAt: now }, now)
  }

  resume(now = Date.now(), captureGeneration = this.captureGeneration + 1): SessionRecord {
    this.assertStatus('paused', 'interrupted')
    if (this.pauseStartedAt !== null) this.accumulatedPauseMs += Math.max(0, now - this.pauseStartedAt)
    this.pauseStartedAt = null
    this.captureGeneration = captureGeneration
    this.ledger.resume(now, captureGeneration)
    this.expCounter.breakContinuity()
    return this.update({ status: 'running', pausedAt: undefined }, now)
  }

  interrupt(now = Date.now()): SessionRecord {
    this.assertStatus('running')
    this.pauseStartedAt = now
    this.ledger.suspend()
    this.expCounter.breakContinuity()
    return this.update({ status: 'interrupted', pausedAt: now }, now)
  }

  ingest(
    state: GameStateSnapshot,
    events: readonly GameEvent[],
    observation?: ValidatedObservation,
    captureGeneration = this.captureGeneration,
  ): SessionRecord {
    return this.ingestWithAccounting(state, events, observation, captureGeneration).record
  }

  ingestWithAccounting(
    state: GameStateSnapshot,
    events: readonly GameEvent[],
    observation?: ValidatedObservation,
    captureGeneration = this.captureGeneration,
  ): SessionIngestResult {
    this.assertStatus('running')
    if (captureGeneration !== this.captureGeneration) this.expCounter.breakContinuity()
    this.captureGeneration = captureGeneration
    const activeAt = this.activeDuration(state.capturedAt)
    const accounting = this.ledger.ingest(state, events, observation, activeAt, captureGeneration)
    this.expCounter.ingest(accounting.exp)
    this.latestAccounting = accounting
    const startState = this.record!.startState ?? state
    this.latestState = state
    const record = this.update({ startState, endState: state }, state.capturedAt)
    return { record, accounting }
  }

  end(now = Date.now()): SessionRecord {
    this.assertStatus('running', 'paused', 'interrupted')
    if (this.pauseStartedAt !== null) this.accumulatedPauseMs += Math.max(0, now - this.pauseStartedAt)
    this.pauseStartedAt = null
    return this.update({ status: 'ended', endedAt: now, pausedAt: undefined }, now)
  }

  current(now = Date.now()): SessionRecord | null {
    if (!this.record) return null
    return this.update({}, now)
  }

  liveRates(now = this.latestState?.capturedAt ?? Date.now()): LiveRateSnapshot {
    return this.ledger.liveRates(now)
  }

  accountingFrame(): AccountingFrame | null {
    return this.latestAccounting
  }

  private update(patch: Partial<SessionRecord>, now: number): SessionRecord {
    if (!this.record) throw new Error('No active record')
    const activeDurationMs = this.currentActiveDuration(now)
    const next = { ...this.record, ...patch, activeDurationMs }
    this.record = { ...next, aggregate: this.aggregate(next, activeDurationMs) }
    return this.record
  }

  private activeDuration(now: number): number {
    if (!this.record) return 0
    const effectiveNow = this.record.status === 'ended' && this.record.endedAt !== undefined
      ? Math.min(now, this.record.endedAt)
      : now
    const currentPause = this.pauseStartedAt === null ? 0 : Math.max(0, effectiveNow - this.pauseStartedAt)
    return Math.max(0, effectiveNow - this.record.startedAt - this.accumulatedPauseMs - currentPause)
  }

  private currentActiveDuration(now: number): number {
    return Math.max(this.record?.activeDurationMs ?? 0, this.activeDuration(now))
  }

  private aggregate(record: SessionRecord, durationMs: number): SessionAggregate {
    const end = this.latestState ?? record.endState
    const aggregate = this.ledger.aggregate()
    return {
      durationMs,
      ...aggregate,
      expGained: this.expCounter.total(),
      expRateGained: aggregate.expGained,
      coverage: end?.coverage ?? emptyCoverage(),
    }
  }

  private emptyAggregate(): SessionAggregate {
    return {
      durationMs: 0,
      expGained: null,
      expRateGained: null,
      expTrustedDurationMs: 0,
      expPerHour: null,
      mesosStartBalance: null,
      mesosCurrentBalance: null,
      mesosBalanceChange: null,
      mesosDelta: null,
      mesosTrustedDurationMs: 0,
      mesosPerHour: null,
      hpConsumed: 0,
      hpConsumedPerHour: null,
      hpRecovered: 0,
      hpRecoveredPerHour: null,
      mpConsumed: 0,
      mpConsumedPerHour: null,
      mpRecovered: 0,
      mpRecoveredPerHour: null,
      coverage: emptyCoverage(),
    }
  }

  private assertStatus(...allowed: SessionRecord['status'][]): void {
    if (!this.record || !allowed.includes(this.record.status)) throw new Error(`Invalid record transition from ${this.record?.status ?? 'none'}`)
  }
}
